import Dexie, { type EntityTable } from "dexie";

/**
 * Local mirror of the notes corpus. Sources hydrate this on startup and
 * mutate it on every edit; the queue worker drains pending ops to the API.
 *
 * Schema versioning: bump the .version() call on any breaking change and
 * add an upgrade function. Existing v1 stores remain readable.
 */

export interface LocalNote {
  id: number;
  parentId: number | null;
  title: string;
  kind: "note" | "canvas" | "folder";
  contentMd: string;
  contentJson: string | null;
  position: number;
  published: boolean;
  tags: string[];
  canvasKind: "reactflow" | "tldraw";
  updatedAt: number;        // unix seconds
  localUpdatedAt: number;   // unix ms — last local mutation
  dirty: 0 | 1;             // 1 = unsynced
  deleted: 0 | 1;           // tombstone for deletes
}

export interface LocalTag { tag: string; count: number }

export interface SyncOp {
  id?: number;
  op: "create" | "update" | "delete";
  noteId: number;           // local id; for create, this is a temp negative id
  payload: Record<string, unknown> | null;
  attempts: number;
  createdAt: number;
  lastError: string | null;
}

export interface MetaRow {
  key: "lastHydratedAt" | "lastSyncAt";
  value: number;
}

class NotesDB extends Dexie {
  notes!: EntityTable<LocalNote, "id">;
  tags!: EntityTable<LocalTag, "tag">;
  queue!: EntityTable<SyncOp, "id">;
  meta!: EntityTable<MetaRow, "key">;

  constructor() {
    super("notes-app");
    this.version(1).stores({
      notes: "id, parentId, title, kind, published, dirty, deleted, updatedAt",
      tags:  "&tag, count",
      queue: "++id, noteId, op, attempts, createdAt",
      meta:  "&key",
    });
  }
}

export const db = new NotesDB();
